import React, { memo } from 'react';

const Pagination = memo(({ 
  currentPage, 
  totalItems, 
  itemsPerPage, 
  onPageChange,
  showInfo = true,
  maxPageNumbers = 5,
  theme = { primary: "#10c9c0" } // Default teal, can be overridden
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Don't render if there's only one page or no items
  if (totalPages <= 1) return null;
  
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  
  // Calculate which page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxPageNumbers / 2));
    let endPage = Math.min(totalPages, startPage + maxPageNumbers - 1);
    
    // Adjust start if we're near the end
    if (endPage - startPage + 1 < maxPageNumbers) {
      startPage = Math.max(1, endPage - maxPageNumbers + 1);
    }
    
    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push('...');
      }
    }
    
    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Add ellipsis and last page if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      pages.push(totalPages);
    }
    
    return pages;
  };
  
  const pageNumbers = getPageNumbers();
  
  const handlePageClick = (page) => {
    if (page !== '...' && page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);
      // Smooth scroll to top of results
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // Generate CSS custom properties for theming
  const themeStyles = {
    '--pagination-primary': theme.primary,
    '--pagination-primary-hover': theme.primary + 'dd', // Add opacity for hover
    '--pagination-primary-light': theme.primary + '20', // Light version for backgrounds
  };
  
  return (
    <div className="flex flex-col items-center space-y-4 mt-8 mb-6" style={themeStyles}>
      {/* Results info */}
      {showInfo && (
        <div className="text-sm text-gray-600 text-center">
          Showing <span className="font-semibold text-gray-900">{startItem}</span> to{' '}
          <span className="font-semibold text-gray-900">{endItem}</span> of{' '}
          <span className="font-semibold text-gray-900">{totalItems}</span> properties
        </div>
      )}
      
      {/* Pagination controls */}
      <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2">
        {/* Previous button */}
        <button
          onClick={() => handlePageClick(currentPage - 1)}
          disabled={currentPage === 1}
          className={`
            flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
            ${currentPage === 1
              ? 'text-gray-400 cursor-not-allowed bg-gray-100'
              : 'text-gray-700 bg-white border border-gray-300 shadow-sm hover:shadow-md transform hover:scale-105'
            }
          `}
          style={currentPage !== 1 ? {
            '--tw-border-opacity': '1',
            borderColor: 'rgb(209 213 219)',
          } : {}}
          onMouseEnter={(e) => {
            if (currentPage !== 1) {
              e.target.style.backgroundColor = theme.primary;
              e.target.style.color = 'white';
              e.target.style.borderColor = theme.primary;
            }
          }}
          onMouseLeave={(e) => {
            if (currentPage !== 1) {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = 'rgb(55 65 81)';
              e.target.style.borderColor = 'rgb(209 213 219)';
            }
          }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden xs:inline">Previous</span>
        </button>
        
        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {pageNumbers.map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="px-2 py-2 text-gray-500 text-sm">...</span>
              ) : (
                <button
                  onClick={() => handlePageClick(page)}
                  className={`
                    w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 transform hover:scale-105
                    ${page === currentPage
                      ? 'text-white shadow-lg scale-105'
                      : 'text-gray-700 bg-white border border-gray-300 shadow-sm hover:shadow-md'
                    }
                  `}
                  style={page === currentPage ? {
                    backgroundColor: theme.primary,
                    borderColor: theme.primary,
                  } : {}}
                  onMouseEnter={(e) => {
                    if (page !== currentPage) {
                      e.target.style.backgroundColor = theme.primary;
                      e.target.style.color = 'white';
                      e.target.style.borderColor = theme.primary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (page !== currentPage) {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.color = 'rgb(55 65 81)';
                      e.target.style.borderColor = 'rgb(209 213 219)';
                    }
                  }}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>
        
        {/* Next button */}
        <button
          onClick={() => handlePageClick(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`
            flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
            ${currentPage === totalPages
              ? 'text-gray-400 cursor-not-allowed bg-gray-100'
              : 'text-gray-700 bg-white border border-gray-300 shadow-sm hover:shadow-md transform hover:scale-105'
            }
          `}
          onMouseEnter={(e) => {
            if (currentPage !== totalPages) {
              e.target.style.backgroundColor = theme.primary;
              e.target.style.color = 'white';
              e.target.style.borderColor = theme.primary;
            }
          }}
          onMouseLeave={(e) => {
            if (currentPage !== totalPages) {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = 'rgb(55 65 81)';
              e.target.style.borderColor = 'rgb(209 213 219)';
            }
          }}
        >
          <span className="hidden xs:inline">Next</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      
      {/* Quick jump for large datasets */}
      {totalPages > 10 && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-600">Jump to page:</span>
          <input
            type="number"
            min="1"
            max={totalPages}
            value={currentPage}
            onChange={(e) => {
              const page = parseInt(e.target.value);
              if (page >= 1 && page <= totalPages) {
                handlePageClick(page);
              }
            }}
            className="w-16 px-2 py-1 text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:border-transparent"
            style={{
              '--tw-ring-color': theme.primary + '40'
            }}
            onFocus={(e) => {
              e.target.style.ringColor = theme.primary + '40';
              e.target.style.borderColor = theme.primary;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgb(209 213 219)';
            }}
          />
          <span className="text-gray-600">of {totalPages}</span>
        </div>
      )}
    </div>
  );
});

Pagination.displayName = 'Pagination';

export default Pagination;